import { useEffect, useState, useMemo } from "react";
import { motion } from "framer-motion";
import {
  ResponsiveContainer, LineChart, Line, CartesianGrid, XAxis, YAxis, Tooltip,
  PieChart, Pie, Cell, Legend, BarChart, Bar,
} from "recharts";
import {
  CreditCard, TrendingUp, IndianRupee, DollarSign, Tag, Download, Filter, RefreshCw, Search,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import api, { formatApiError } from "@/utils/api";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TILE_DEFS = [
  { key: "all_time", label: "All-time revenue", icon: TrendingUp, color: "#A855F7" },
  { key: "month",    label: "Last 30 days",    icon: CreditCard, color: "#4ECDC4" },
  { key: "week",     label: "Last 7 days",     icon: CreditCard, color: "#FFE66D" },
  { key: "today",    label: "Today",           icon: CreditCard, color: "#FF6B6B" },
];

const fmtINR = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
const fmtUSD = (n) => `$${Number(n || 0).toFixed(2)}`;

const PaymentsPanel = () => {
  const [stats, setStats] = useState(null);
  const [items, setItems] = useState([]);
  const [days, setDays] = useState(30);
  const [filters, setFilters] = useState({ status: "", gateway: "", coupon: "", user_email: "" });
  const [loading, setLoading] = useState(false);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [s, l] = await Promise.all([
        api.get(`${API}/admin/payments/stats?days=${days}`),
        api.get(`${API}/admin/payments`, { params: { days, ...stripEmpty(filters) } }),
      ]);
      setStats(s.data);
      setItems(l.data.items || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  // Initial + when days changes
  useEffect(() => { loadAll(); /* eslint-disable-next-line */ }, [days]);

  const stripEmpty = (o) => Object.fromEntries(Object.entries(o).filter(([, v]) => v !== "" && v != null));

  const onApplyFilters = () => loadAll();

  const onExport = async () => {
    try {
      const resp = await api.get(`${API}/admin/payments/export?days=90`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([resp.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url; a.download = `tots360_payments_90d.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Export downloaded");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const dailyChartData = useMemo(() => stats?.daily_revenue || [], [stats]);

  const gatewayPie = useMemo(() => {
    if (!stats) return [];
    const map = {};
    (stats.gateway_split || []).forEach((g) => {
      const k = `${g.gateway} (${g.currency})`;
      map[k] = (map[k] || 0) + g.count;
    });
    return Object.entries(map).map(([name, value]) => ({ name, value }));
  }, [stats]);

  const couponBars = useMemo(() => {
    if (!stats) return [];
    return (stats.coupon_split || []).map((c) => ({
      name: c.coupon, count: c.count,
      discount: c.discount_inr || c.discount_usd || 0,
    }));
  }, [stats]);

  return (
    <div className="space-y-6" data-testid="payments-panel">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-xl font-display flex items-center gap-2">
          <CreditCard className="w-5 h-5 text-[#4ECDC4]" /> Payments &amp; Revenue
        </h2>
        <div className="flex items-center gap-2">
          <Select value={String(days)} onValueChange={(v) => setDays(parseInt(v))}>
            <SelectTrigger className="w-36 bg-white/[0.03] border-white/10 text-white" data-testid="days-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[#0F1C3F] border-white/10 text-white">
              {[7, 30, 90, 180, 365].map((d) => <SelectItem key={d} value={String(d)}>Last {d} days</SelectItem>)}
            </SelectContent>
          </Select>
          <Button onClick={loadAll} variant="outline" size="icon"
            className="border-white/15 text-white/70 hover:bg-white/5"
            data-testid="refresh-btn"><RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /></Button>
          <Button onClick={onExport} variant="outline"
            className="border-white/15 text-white/70 hover:bg-white/5" data-testid="export-csv-btn">
            <Download className="w-4 h-4 mr-1" /> Export CSV
          </Button>
        </div>
      </div>

      {/* Tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {TILE_DEFS.map((t) => {
          const inr = stats?.tiles?.[`${t.key}_inr`] || { sum: 0, count: 0 };
          const usd = stats?.tiles?.[`${t.key}_usd`] || { sum: 0, count: 0 };
          const Icon = t.icon;
          return (
            <motion.div key={t.key} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
              className="bg-white/[0.03] border border-white/10 rounded-xl p-4"
              data-testid={`tile-${t.key}`}>
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs text-white/40 uppercase tracking-wider">{t.label}</p>
                <Icon className="w-4 h-4" style={{ color: t.color }} />
              </div>
              <div className="flex items-baseline gap-1 flex-wrap">
                <span className="text-xl font-bold text-white">{fmtINR(inr.sum)}</span>
                <span className="text-xs text-white/40">+ {fmtUSD(usd.sum)}</span>
              </div>
              <p className="text-xs text-white/40 mt-1">{inr.count + usd.count} txn</p>
            </motion.div>
          );
        })}
      </div>

      {/* Charts */}
      <div className="grid lg:grid-cols-3 gap-4">
        {/* Revenue over time */}
        <div className="lg:col-span-2 bg-white/[0.03] border border-white/10 rounded-xl p-4">
          <h3 className="text-sm font-semibold text-white mb-3">Revenue over time (₹ + $ stacked)</h3>
          <div className="h-64" style={{ minHeight: 256 }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={dailyChartData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                <XAxis dataKey="date" tick={{ fill: "rgba(255,255,255,0.4)", fontSize: 10 }} />
                <YAxis tick={{ fill: "rgba(255,255,255,0.4)", fontSize: 10 }} />
                <Tooltip contentStyle={{ backgroundColor: "#0F1C3F", border: "1px solid rgba(255,255,255,0.1)", color: "#fff" }} />
                <Line type="monotone" dataKey="inr" stroke="#4ECDC4" strokeWidth={2} dot={false} name="INR" />
                <Line type="monotone" dataKey="usd" stroke="#A855F7" strokeWidth={2} dot={false} name="USD" />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Gateway split */}
        <div className="bg-white/[0.03] border border-white/10 rounded-xl p-4">
          <h3 className="text-sm font-semibold text-white mb-3">Gateway split (txn count)</h3>
          <div className="h-64" style={{ minHeight: 256 }}>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={gatewayPie} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={70}>
                  {gatewayPie.map((_, i) => (
                    <Cell key={i} fill={["#4ECDC4", "#A855F7", "#FFE66D", "#FF6B6B"][i % 4]} />
                  ))}
                </Pie>
                <Tooltip contentStyle={{ backgroundColor: "#0F1C3F", border: "1px solid rgba(255,255,255,0.1)", color: "#fff" }} />
                <Legend wrapperStyle={{ fontSize: 11, color: "#fff" }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Coupon breakdown bars */}
      <div className="bg-white/[0.03] border border-white/10 rounded-xl p-4">
        <h3 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
          <Tag className="w-4 h-4 text-[#FFE66D]" /> Coupon redemption (last {days} days)
        </h3>
        <div className="h-44" style={{ minHeight: 176 }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={couponBars}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
              <XAxis dataKey="name" tick={{ fill: "rgba(255,255,255,0.4)", fontSize: 11 }} />
              <YAxis tick={{ fill: "rgba(255,255,255,0.4)", fontSize: 10 }} />
              <Tooltip contentStyle={{ backgroundColor: "#0F1C3F", border: "1px solid rgba(255,255,255,0.1)", color: "#fff" }} />
              <Bar dataKey="count" fill="#FFE66D" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Filters + Table */}
      <div className="bg-white/[0.03] border border-white/10 rounded-xl p-4">
        <div className="flex flex-wrap items-end gap-3 mb-3">
          <div className="flex-1 min-w-[180px]">
            <Label className="text-xs text-white/50 mb-1">User email</Label>
            <div className="relative">
              <Search className="w-3.5 h-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-white/40" />
              <Input value={filters.user_email} onChange={(e) => setFilters({ ...filters, user_email: e.target.value })}
                placeholder="search email…" className="pl-7 bg-[#0F1C3F] border-white/10 text-white text-sm h-9"
                data-testid="filter-email" />
            </div>
          </div>
          <SelectFilter label="Status" value={filters.status}
            onChange={(v) => setFilters({ ...filters, status: v })}
            options={["", "paid", "created", "signature_failed", "expired"]}
            testid="filter-status" />
          <SelectFilter label="Gateway" value={filters.gateway}
            onChange={(v) => setFilters({ ...filters, gateway: v })}
            options={["", "razorpay", "stripe"]}
            testid="filter-gateway" />
          <SelectFilter label="Coupon" value={filters.coupon}
            onChange={(v) => setFilters({ ...filters, coupon: v })}
            options={["", "TOTS360"]}
            testid="filter-coupon" />
          <Button onClick={onApplyFilters} className="h-9 bg-[#4ECDC4] hover:bg-[#3db8b0] text-[#0A1029]"
            data-testid="apply-filters-btn"><Filter className="w-3.5 h-3.5 mr-1" /> Apply</Button>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="payments-table">
            <thead>
              <tr className="text-left text-xs uppercase text-white/40 border-b border-white/10">
                <th className="py-2 pr-2">Date</th>
                <th className="py-2 pr-2">User</th>
                <th className="py-2 pr-2">Role</th>
                <th className="py-2 pr-2">Qty</th>
                <th className="py-2 pr-2">Gateway</th>
                <th className="py-2 pr-2">Coupon</th>
                <th className="py-2 pr-2 text-right">Total</th>
                <th className="py-2 pr-2">Status</th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 && (
                <tr><td colSpan={8} className="py-8 text-center text-white/40">No transactions in this window.</td></tr>
              )}
              {items.map((it) => (
                <tr key={it.transaction_id} className="border-b border-white/[0.04] hover:bg-white/[0.02]">
                  <td className="py-2 pr-2 text-white/60 text-xs">{(it.created_at || "").slice(0, 16).replace("T", " ")}</td>
                  <td className="py-2 pr-2 text-white">{it.user_email}</td>
                  <td className="py-2 pr-2 text-white/60 text-xs">{it.user_role}</td>
                  <td className="py-2 pr-2 text-white">{it.qty}</td>
                  <td className="py-2 pr-2"><Badge>{it.gateway}</Badge></td>
                  <td className="py-2 pr-2 text-[#FFE66D] text-xs">{it.coupon_code || "—"}</td>
                  <td className="py-2 pr-2 text-right text-white font-semibold">
                    {it.currency === "INR" ? fmtINR(it.total) : fmtUSD(it.total)}
                  </td>
                  <td className="py-2 pr-2"><StatusBadge s={it.status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

const SelectFilter = ({ label, value, onChange, options, testid }) => (
  <div className="min-w-[120px]">
    <Label className="text-xs text-white/50 mb-1">{label}</Label>
    <Select value={value} onValueChange={(v) => onChange(v === "__all" ? "" : v)}>
      <SelectTrigger className="bg-[#0F1C3F] border-white/10 text-white text-sm h-9" data-testid={testid}>
        <SelectValue placeholder="All" />
      </SelectTrigger>
      <SelectContent className="bg-[#0F1C3F] border-white/10 text-white">
        {options.map((o) => (
          <SelectItem key={o || "__all"} value={o || "__all"}>{o || "All"}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  </div>
);

const Badge = ({ children }) => (
  <span className="inline-block px-2 py-0.5 rounded text-[11px] bg-white/[0.06] border border-white/10 text-white/70">
    {children}
  </span>
);

const StatusBadge = ({ s }) => {
  const map = {
    paid: { c: "#4ECDC4", bg: "rgba(78,205,196,0.12)" },
    created: { c: "#FFE66D", bg: "rgba(255,230,109,0.12)" },
    signature_failed: { c: "#FF6B6B", bg: "rgba(255,107,107,0.12)" },
    expired: { c: "#FF6B6B", bg: "rgba(255,107,107,0.12)" },
  };
  const m = map[s] || { c: "#94a3b8", bg: "rgba(148,163,184,0.12)" };
  return (
    <span className="inline-block px-2 py-0.5 rounded text-[11px] font-medium"
      style={{ color: m.c, backgroundColor: m.bg }}>{s}</span>
  );
};

export default PaymentsPanel;
